import { isRecord, readStoredJson } from './persistedState';

export type UtilityTab = 'settings' | 'prototype' | 'timeline' | 'json-import';
export type WorkspaceTab = { id: string; kind: 'entity' | 'utility'; entityId?: string; utility?: UtilityTab; createdAt: number; order: number; groupId: string | null; pinned: boolean };
export type TabGroup = { id: string; name: string; collapsed: boolean; color: string };
export const tabGroupPalette = ['#7657d6', '#287e70', '#b6652f', '#a94b72', '#3975aa', '#7b5a9f', '#8a7a2f', '#347f9e'];
const utilityTabs: UtilityTab[] = ['settings', 'prototype', 'timeline', 'json-import'];
function isUtilityTab(value: unknown): value is UtilityTab { return typeof value === 'string' && utilityTabs.some((tab) => tab === value); }

export function readOpenTabs(storage: Storage): WorkspaceTab[] {
  const saved = readStoredJson(storage, 'chronis.workspace.tabs.v2') ?? readStoredJson(storage, 'chronis.workspace.tabs.v1');
  if (!Array.isArray(saved)) return [];
  const savedItems: unknown[] = saved;
  return savedItems.flatMap<WorkspaceTab>((item, index) => {
    const defaults = { createdAt: Date.now() + index, order: index, groupId: null, pinned: false };
    if (typeof item === 'string' && item) return [{ id: item, kind: 'entity', entityId: item, ...defaults }];
    if (!isRecord(item) || typeof item.id !== 'string' || !item.id) return [];
    const createdAt = typeof item.createdAt === 'number' && Number.isFinite(item.createdAt) ? item.createdAt : defaults.createdAt;
    const order = typeof item.order === 'number' && Number.isFinite(item.order) ? item.order : defaults.order;
    const groupId = typeof item.groupId === 'string' ? item.groupId : null;
    const pinned = item.pinned === true;
    if (item.kind === 'entity' && (item.entityId === undefined || typeof item.entityId === 'string')) {
      return [{ id: item.id, kind: 'entity' as const, entityId: item.entityId, createdAt, order, groupId, pinned }];
    }
    if (item.kind === 'utility' && isUtilityTab(item.utility)) {
      return [{ id: item.id, kind: 'utility', utility: item.utility, createdAt, order, groupId, pinned }];
    }
    return [];
  });
}
export function readTabGroups(storage: Storage): TabGroup[] {
  const saved = readStoredJson(storage, 'chronis.workspace.tab-groups.v1');
  if (!Array.isArray(saved)) return [];
  return saved.flatMap((item, index) => isRecord(item) && typeof item.id === 'string' && item.id && typeof item.name === 'string' && item.name
    ? [{ id: item.id, name: item.name, collapsed: item.collapsed === true, color: typeof item.color === 'string' ? item.color : tabGroupPalette[index % tabGroupPalette.length]! }]
    : []);
}
export function persistTabs(storage: Storage, tabs: WorkspaceTab[], groups: TabGroup[], limit: number, activeId: string | null) {
  try {
    storage.setItem('chronis.workspace.tabs.v2', JSON.stringify(tabs));
    storage.setItem('chronis.workspace.tab-groups.v1', JSON.stringify(groups));
    storage.setItem('chronis.workspace.tab-limit.v1', String(limit));
    if (activeId) storage.setItem('chronis.workspace.active-tab.v1', activeId);
    else storage.removeItem('chronis.workspace.active-tab.v1');
  } catch { /* Speicher ist optional. */ }
}
export function sortedTabs(tabs: WorkspaceTab[]) { return [...tabs].sort((a, b) => Number(b.pinned) - Number(a.pinned) || a.order - b.order); }
export function nextTabOrder(tabs: WorkspaceTab[]) { return Math.max(-1, ...tabs.map((tab) => tab.order)) + 1; }
export function enforceTabLimit(tabs: WorkspaceTab[], activeId: string | null, limit: number) {
  let next = [...tabs];
  let selected = activeId;
  const closable = next.filter((tab) => !tab.pinned).sort((a, b) => a.createdAt - b.createdAt);
  while (next.length > limit && closable.length) {
    const oldest = closable.shift()!;
    next = next.filter((tab) => tab.id !== oldest.id);
    if (selected === oldest.id) selected = next[0]?.id ?? null;
  }
  return { tabs: next, activeId: selected };
}
export function removeTab(tabs: WorkspaceTab[], activeId: string | null, id: string) {
  const index = tabs.findIndex((tab) => tab.id === id);
  const closing = tabs[index];
  if (!closing || closing.pinned) return null;
  const nextTabs = tabs.filter((tab) => tab.id !== id);
  const next = activeId === id ? nextTabs[index] ?? nextTabs[index - 1] ?? null : null;
  return { tabs: nextTabs, activeId: activeId === id ? next?.id ?? null : activeId, next, wasActive: activeId === id };
}
export function removeTabsWhere(tabs: WorkspaceTab[], activeId: string | null, predicate: (tab: WorkspaceTab) => boolean) {
  const nextTabs = tabs.filter((tab) => tab.pinned || !predicate(tab));
  const nextId = activeId && nextTabs.some((tab) => tab.id === activeId) ? activeId : nextTabs[0]?.id ?? null;
  return { tabs: nextTabs, activeId: nextId, next: nextTabs.find((tab) => tab.id === nextId) ?? null };
}
export function reorderTabs(tabs: WorkspaceTab[], sourceId: string, targetId: string) {
  if (sourceId === targetId) return null;
  const source = tabs.find((tab) => tab.id === sourceId);
  const target = tabs.find((tab) => tab.id === targetId);
  if (!source || !target) return null;
  const ordered = sortedTabs(tabs).filter((tab) => tab.id !== sourceId);
  ordered.splice(ordered.findIndex((tab) => tab.id === targetId), 0, { ...source, groupId: target.groupId, pinned: target.pinned });
  return ordered.map((tab, order) => ({ ...tab, order }));
}
